import { getBackendUrl, resolveAssetUrl } from '../lib/api';
import React, { useState, useEffect, useMemo } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import NotificationBell from '../components/NotificationBell';
import InAppNotifications from '../components/InAppNotifications';
import BrandSelector from '../components/BrandSelector';
import PWAInstallPrompt from '../components/PWAInstallPrompt';
import PWAInstallBanner from '../components/PWAInstallBanner';
import GlobalSearchBar from '../components/admin/GlobalSearchBar';
import {
  LayoutDashboard,
  Users,
  Briefcase,
  Building,
  Settings,
  LogOut,
  UserPlus,
  UsersRound,
  Globe,
  BarChart3,
  Code2,
  Menu,
  X,
  Send,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';

/**
 * Sidebar admin compacte — fusion des entrées en groupes :
 *   - Talents       (Candidats / Talent Pool / Qualifications)
 *   - Communications (Messagerie / Communications)
 *   - Analytics     (Vue stratégique / Analytics / Conversion)
 *
 * Tracking est rangé dans Paramètres (Settings) plutôt qu'en top-level.
 */

const NAV_GROUPS = [
  { kind: 'item', path: '/admin', label: 'Tableau de bord', icon: LayoutDashboard, exact: true },
  { kind: 'item', path: '/admin/leads', label: 'Leads', icon: UserPlus },
  { kind: 'item', path: '/admin/clients', label: 'Clients', icon: Building },
  { kind: 'item', path: '/admin/missions', label: 'Missions', icon: Briefcase },
  {
    kind: 'group',
    id: 'talents',
    label: 'Talents',
    icon: Users,
    children: [
      { path: '/admin/candidates', label: 'Candidats' },
      { path: '/admin/talent-pool', label: 'Talent Pool' },
      { path: '/admin/qualifications', label: 'Qualifications' },
      { path: '/admin/candidates-rejetes', label: 'Candidats rejetés' },
    ],
  },
  {
    kind: 'group',
    id: 'comms',
    label: 'Communications',
    icon: Send,
    children: [
      { path: '/admin/messages', label: 'Messagerie' },
      { path: '/admin/communications', label: 'Logs envoyés' },
    ],
  },
  {
    kind: 'group',
    id: 'analytics',
    label: 'Analytics',
    icon: BarChart3,
    children: [
      { path: '/admin/strategic-view', label: 'Vue stratégique' },
      { path: '/admin/analytics', label: 'Analytics globales' },
      { path: '/admin/conversion', label: 'Conversion (5 chiffres)' },
    ],
  },
  { kind: 'item', path: '/admin/users', label: 'Utilisateurs', icon: UsersRound },
  { kind: 'item', path: '/admin/brands', label: 'Marques', icon: Globe },
  {
    kind: 'group',
    id: 'settings',
    label: 'Paramètres',
    icon: Settings,
    children: [
      { path: '/admin/settings', label: 'Configuration' },
      { path: '/admin/tracking', label: 'Tracking & Pixels' },
    ],
  },
];

const isPathActive = (current, target, exact = false) => {
  if (exact) return current === target;
  if (current === target) return true;
  return current.startsWith(target + '/');
};

const AdminLayout = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [logoUrl, setLogoUrl] = useState(null);
  const [platformName, setPlatformName] = useState('Talent Partner');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);

  // Determine which group should be expanded based on current path
  const initiallyOpenGroupId = useMemo(() => {
    for (const node of NAV_GROUPS) {
      if (node.kind === 'group') {
        const hit = node.children.some((c) => isPathActive(location.pathname, c.path));
        if (hit) return node.id;
      }
    }
    return null;
  }, [location.pathname]);

  const [openGroup, setOpenGroup] = useState(initiallyOpenGroupId);

  useEffect(() => {
    setOpenGroup(initiallyOpenGroupId);
  }, [initiallyOpenGroupId]);

  useEffect(() => {
    fetch(getBackendUrl() + '/api/branding')
      .then((res) => res.json())
      .then((data) => {
        if (data.logo_dark_url) setLogoUrl(data.logo_dark_url);
        else if (data.logo_icon_url) setLogoUrl(data.logo_icon_url);
        if (data.platform_name) setPlatformName(data.platform_name);
      })
      .catch((err) => console.error('Failed to fetch branding:', err));
  }, []);

  // Close mobile sidebar on route change
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const renderNavItem = (item) => {
    const Icon = item.icon;
    const active = isPathActive(location.pathname, item.path, item.exact);
    return (
      <li key={item.path}>
        <Link
          to={item.path}
          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
            active
              ? 'bg-tp-accent text-white'
              : 'text-white/80 hover:bg-white/10 hover:text-white'
          }`}
          data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
        >
          <Icon className="h-5 w-5 flex-shrink-0" />
          <span className="truncate">{item.label}</span>
        </Link>
      </li>
    );
  };

  const renderGroup = (group) => {
    const Icon = group.icon;
    const isOpen = openGroup === group.id;
    const hasActiveChild = group.children.some((c) => isPathActive(location.pathname, c.path));
    const Caret = isOpen ? ChevronDown : ChevronRight;

    return (
      <li key={group.id}>
        <button
          type="button"
          onClick={() => setOpenGroup(isOpen ? null : group.id)}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
            hasActiveChild
              ? 'bg-white/10 text-white'
              : 'text-white/80 hover:bg-white/10 hover:text-white'
          }`}
          aria-expanded={isOpen}
          data-testid={`admin-nav-group-${group.id}`}
        >
          <Icon className="h-5 w-5 flex-shrink-0" />
          <span className="flex-1 text-left truncate">{group.label}</span>
          <Caret className="h-4 w-4 flex-shrink-0 opacity-70" />
        </button>

        {isOpen && (
          <ul className="mt-1 ml-4 pl-3 border-l border-white/10 space-y-1">
            {group.children.map((child) => {
              const active = isPathActive(location.pathname, child.path);
              return (
                <li key={child.path}>
                  <Link
                    to={child.path}
                    className={`block px-3 py-2 rounded-md text-[13px] transition-colors ${
                      active
                        ? 'bg-tp-accent text-white font-medium'
                        : 'text-white/70 hover:bg-white/10 hover:text-white'
                    }`}
                    data-testid={`admin-nav-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {child.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </li>
    );
  };

  const SidebarContent = () => (
    <>
      {/* Mobile close */}
      <div className="lg:hidden flex items-center justify-between p-3 border-b border-white/10">
        <span className="text-sm text-white/80">Menu</span>
        <button
          onClick={() => setSidebarOpen(false)}
          className="p-2 rounded-lg hover:bg-white/10 transition-colors"
          aria-label="Fermer le menu"
          data-testid="mobile-sidebar-close"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="p-4 lg:p-6 border-b border-white/10">
        <Link to="/" className="flex items-center gap-3">
          {logoUrl ? (
            <img
              src={resolveAssetUrl(logoUrl)}
              alt={platformName}
              className="h-14 lg:h-16 max-w-[220px] lg:max-w-[260px] object-contain"
              data-testid="admin-logo-image"
            />
          ) : (
            <span className="text-lg lg:text-xl font-display font-semibold">{platformName}</span>
          )}
        </Link>
        <p className="text-xs text-white/60 mt-1">Tableau de bord Admin</p>
        <div className="flex items-center gap-2 mt-2">
          <BrandSelector selectedBrand={selectedBrand} onBrandChange={setSelectedBrand} />
          <div className="ml-auto">
            <InAppNotifications />
          </div>
        </div>
      </div>

      <nav className="p-3 lg:p-4 flex-1 overflow-y-auto">
        <ul className="space-y-1">
          {NAV_GROUPS.map((node) =>
            node.kind === 'item' ? renderNavItem(node) : renderGroup(node)
          )}
        </ul>
      </nav>

      {/* User info and logout */}
      <div className="p-4 border-t border-white/10 mt-auto">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{user?.full_name || 'Admin'}</p>
            <p className="text-xs text-white/60 truncate">{user?.email}</p>
          </div>
          <div className="flex items-center gap-1">
            <InAppNotifications className="hidden lg:block" />
            <NotificationBell className="hidden lg:block" />
            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              className="text-white/80 hover:text-white hover:bg-white/10"
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen bg-tp-bg" data-testid="admin-layout">
      {/* Mobile header */}
      <div
        className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-tp-primary text-white flex items-center justify-between px-4 min-h-safe-header pt-[env(safe-area-inset-top,0px)]"
      >
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 -ml-2 rounded-lg hover:bg-white/10 transition-colors"
            aria-label="Toggle menu"
            data-testid="mobile-sidebar-toggle"
          >
            <Menu className="h-6 w-6" />
          </button>
          <Link to="/admin" className="flex items-center">
            {logoUrl ? (
              <img
                src={resolveAssetUrl(logoUrl)}
                alt={platformName}
                className="h-9 max-w-[150px] object-contain"
              />
            ) : (
              <span className="text-lg font-display font-semibold">{platformName}</span>
            )}
          </Link>
        </div>
        <div className="flex items-center gap-1">
          <InAppNotifications />
          <NotificationBell />
        </div>
      </div>

      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 z-30 bg-black/50 transition-opacity"
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-overlay"
        />
      )}

      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-40
          w-64 bg-tp-primary text-white
          transform transition-transform duration-300 ease-in-out
          lg:transform-none lg:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          flex flex-col
          pt-safe-header lg:pt-0
        `}
        data-testid="admin-sidebar"
      >
        <SidebarContent />
      </aside>

      <main className="flex-1 overflow-auto pt-safe-header lg:pt-0">
        <div className="hidden lg:flex items-center justify-between px-6 py-3 bg-white border-b border-tp-border sticky top-0 z-20">
          <GlobalSearchBar />
        </div>
        <div className="lg:hidden">
          <PWAInstallBanner />
        </div>
        <Outlet />
      </main>

      <PWAInstallPrompt />
    </div>
  );
};

export default AdminLayout;
